import { z } from 'zod';
import type { FieldDef } from './noteTypes.js';
import { isValidFolder, isValidTitle, normFolder } from './text.js';

export const rulesetSchema = z.enum(['5e-2014', '5e-2024', 'mixed']);
export type Ruleset = z.infer<typeof rulesetSchema>;

export const visibilitySchema = z.enum(['dm', 'all', 'players']);

const titleSchema = z
  .string()
  .transform((s) => s.normalize('NFC').trim())
  .refine(isValidTitle, { message: 'Ungültiger Titel (keine Zeichen \\ / : * ? " < > | # ^ [ ])' });

const folderSchema = z
  .string()
  .transform((s) => normFolder(s))
  .refine(isValidFolder, { message: 'Ungültiger Ordnerpfad' });

const frontmatterSchema = z.record(z.string(), z.unknown());

// ---------- Auth ----------

export const loginSchema = z.object({
  name: z.string().trim().min(1).max(100),
  password: z.string().min(1).max(500),
});

export const setupSchema = z.object({
  name: z.string().trim().min(1).max(100),
  password: z.string().min(10, 'Mindestens 10 Zeichen').max(500),
});
export interface AuthState {
  needsSetup: boolean;
  user: { id: string; name: string; isAdmin: boolean } | null;
}

const newPasswordSchema = z.string().min(10, 'Mindestens 10 Zeichen').max(500);

export const changePasswordSchema = z.object({
  current: z.string().min(1).max(500),
  next: newPasswordSchema,
});

// ---------- Phase 5: Mitglieder, Einladungen, Benutzer ----------

export const campaignRoleSchema = z.enum(['dm', 'co-dm', 'player']);

export interface MemberDTO {
  userId: string;
  name: string;
  role: 'dm' | 'co-dm' | 'player';
}

export const updateMemberSchema = z.object({ role: campaignRoleSchema });

export const createInviteSchema = z.object({
  role: z.enum(['player', 'co-dm']).default('player'),
  label: z.string().trim().max(100).default(''),
  /** null/fehlend = beliebig oft (bis zum Ablauf). */
  maxUses: z.number().int().min(1).max(100).nullable().default(1),
  expiresInDays: z.number().int().min(1).max(90).default(7),
});

export interface InviteDTO {
  id: string;
  role: 'dm' | 'co-dm' | 'player';
  label: string;
  maxUses: number | null;
  uses: number;
  expiresAt: string;
  createdAt: string;
}

/** Antwort beim Anlegen: Der Link ist nur jetzt abrufbar. */
export interface CreatedInviteDTO extends InviteDTO {
  token: string;
}

/** Öffentliche Infos zu einem Einladungslink (Beitrittsseite). */
export interface InviteInfoDTO {
  campaignName: string;
  role: 'dm' | 'co-dm' | 'player';
  /** Ist der angemeldete Benutzer schon Mitglied? */
  alreadyMember: boolean;
}

/** Beitreten: angemeldet ohne Angaben, sonst mit neuem Konto. */
export const acceptInviteSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    password: newPasswordSchema,
  })
  .optional();

export interface UserDTO {
  id: string;
  name: string;
  isAdmin: boolean;
  createdAt: string;
  campaigns: { id: string; name: string; role: 'dm' | 'co-dm' | 'player' }[];
}

/** Freigabestand einer Notiz je Spieler (für den DM). */
export interface NoteAccessDTO {
  userId: string;
  name: string;
  canSee: boolean;
  grantedAt: string | null;
  seenAt: string | null;
}

/** „Entdeckt“: für den Spieler freigegebene Notiz. */
export interface DiscoveryDTO {
  id: string;
  title: string;
  folder: string;
  typeKey: string;
  grantedAt: string;
  seen: boolean;
}

export const updateUserSchema = z.object({
  isAdmin: z.boolean().optional(),
  password: newPasswordSchema.optional(),
});

// ---------- Kampagnen ----------

export const createCampaignSchema = z.object({
  name: z.string().trim().min(1).max(200),
  ruleset: rulesetSchema.default('mixed'),
});

export const updateCampaignSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  ruleset: rulesetSchema.optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});

export interface CampaignDTO {
  id: string;
  name: string;
  ruleset: Ruleset;
  settings: Record<string, unknown>;
  role: 'dm' | 'co-dm' | 'player';
  createdAt: string;
  noteCount?: number;
  homeNoteId?: string | null;
  /** Geheimer Link der Spieleransicht (Karte für TV/remote), falls eingerichtet. */
  screenToken?: string | null;
}

export interface NoteTypeDTO {
  id: string;
  key: string;
  label: string;
  color: string;
  fields: FieldDef[];
  templateMd: string;
}

export const updateNoteTypeSchema = z.object({
  label: z.string().trim().min(1).max(100).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  templateMd: z.string().max(100_000).optional(),
});

// ---------- Notizen ----------

export const createNoteSchema = z.object({
  title: titleSchema,
  folder: folderSchema.default(''),
  typeKey: z.string().max(50).optional(),
  body: z.string().max(2_000_000).optional(),
  frontmatter: frontmatterSchema.optional(),
  visibility: visibilitySchema.optional(),
});

export const updateNoteSchema = z.object({
  title: titleSchema.optional(),
  folder: folderSchema.optional(),
  typeKey: z.string().max(50).optional(),
  body: z.string().max(2_000_000).optional(),
  frontmatter: frontmatterSchema.optional(),
  visibility: visibilitySchema.optional(),
  visiblePlayerIds: z.array(z.string().uuid()).nullable().optional(),
  /** Nur DM: Notiz einem Spieler zuweisen (z. B. seinen Charakter), null = niemandem. */
  ownerId: z.string().uuid().nullable().optional(),
  /** Optimistische Sperre: Stand, auf dem die Änderung basiert. */
  baseUpdatedAt: z.string().optional(),
});

export type CreateNoteInput = z.input<typeof createNoteSchema>;
export type UpdateNoteInput = z.input<typeof updateNoteSchema>;

export interface NoteIndexEntry {
  id: string;
  title: string;
  folder: string;
  typeKey: string;
  visibility: 'dm' | 'all' | 'players';
  updatedAt: string;
  /** Spieler: freigegeben, aber noch nicht geöffnet. */
  unseen?: boolean;
}

export type ResolvedLinkTarget =
  | { type: 'note'; id: string; title: string; folder: string; typeKey: string }
  | { type: 'file'; id: string; name: string; mime: string; url: string };

export interface BacklinkEntry {
  id: string;
  title: string;
  folder: string;
  typeKey: string;
  /** Textausschnitt um die Fundstelle (nur bei Erwähnungen). */
  snippet?: string;
  /** Frontmatter-Felder der Quelle, über die verlinkt wird (z. B. `leader`). */
  properties?: string[];
}

export interface NoteRef {
  id: string;
  title: string;
  typeKey: string;
}

export interface NoteDetail {
  id: string;
  campaignId: string;
  title: string;
  folder: string;
  typeKey: string;
  visibility: 'dm' | 'all' | 'players';
  visiblePlayerIds: string[] | null;
  /** Spieler, dem die Notiz gehört (darf sie bearbeiten). */
  ownerId: string | null;
  /** Darf der Betrachter die Notiz bearbeiten bzw. löschen? */
  canEdit: boolean;
  canDelete: boolean;
  frontmatter: Record<string, unknown>;
  body: string;
  createdAt: string;
  updatedAt: string;
  /** Auflösung aller Link-Ziele der Notiz, Schlüssel = `linkKey(rawTarget)`. */
  links: Record<string, ResolvedLinkTarget>;
  backlinks: {
    /** Notizen mit aufgelöstem Link auf diese Notiz. */
    linked: BacklinkEntry[];
    /** Notizen, die den Titel nur im Text erwähnen (ohne Link). */
    mentions: BacklinkEntry[];
  };
  /** Hierarchie über das Feld `parent` (z. B. Region > Stadt > Taverne). */
  hierarchy: {
    /** Vorfahren von oben nach unten (ohne die Notiz selbst). */
    parents: NoteRef[];
    children: BacklinkEntry[];
  };
}

export interface HierarchyDTO {
  /** Kind → Eltern (nur aufgelöste `parent`-Links). */
  edges: { child: string; parent: string }[];
}

export interface GraphDTO {
  nodes: { id: string; title: string; folder: string; typeKey: string; degree: number }[];
  edges: { source: string; target: string; kind: 'text' | 'property' | 'embed' }[];
  /** Unaufgelöste Ziele als Geister-Knoten. */
  unresolved: { key: string; label: string; sources: string[] }[];
}

export interface SearchHit {
  id: string;
  title: string;
  folder: string;
  typeKey: string;
  /** Ausschnitt mit `<mark>`-Hervorhebungen (serverseitig escaped). */
  snippet: string;
  rank: number;
}

export interface OpenLink {
  rawTarget: string;
  sources: { id: string; title: string }[];
}

// ---------- Import ----------

export const importFolderConfigSchema = z.object({
  typeKey: z.string().max(50),
  visibility: visibilitySchema,
});

export const importCommitSchema = z
  .object({
    token: z.string().regex(/^[a-f0-9]{32}$/),
    campaignId: z.string().uuid().optional(),
    newCampaignName: z.string().trim().min(1).max(200).optional(),
    ruleset: rulesetSchema.optional(),
    folders: z.record(z.string(), importFolderConfigSchema).default({}),
  })
  .refine((v) => !!v.campaignId !== !!v.newCampaignName, {
    message: 'Entweder campaignId oder newCampaignName angeben',
  });

export interface ImportPreviewFolder {
  folder: string;
  noteCount: number;
  fileCount: number;
  suggestedTypeKey: string;
  suggestedVisibility: 'dm' | 'all' | 'players';
  color: string | null;
}

export interface ImportPreview {
  token: string;
  vaultName: string;
  noteCount: number;
  fileCount: number;
  homeNote: string | null;
  folders: ImportPreviewFolder[];
  skipped: { path: string; reason: string }[];
}

export interface ImportReport {
  campaignId: string;
  notes: { created: number; updated: number; unchanged: number; total: number };
  files: { created: number; updated: number; unchanged: number; total: number };
  links: { total: number; resolved: number; unresolved: number };
  openLinks: { from: string; target: string }[];
  conflicts: { from: string; target: string; chosen: string; candidates: string[] }[];
  skipped: { path: string; reason: string }[];
  /** Notizen, die in der Kampagne existieren, aber nicht (mehr) im Vault sind. */
  notInVault: string[];
  warnings: string[];
}

// ---------- Kampf (Phase 2) ----------

const conditionSchema = z.object({
  key: z.string().trim().min(1).max(60),
  rounds: z.number().int().min(0).max(1000).nullable(),
  note: z.string().max(200).optional(),
});

export const createCombatSchema = z.object({
  name: z.string().trim().min(1).max(200).default('Kampf'),
  sessionNoteId: z.string().uuid().nullable().optional(),
  mapNoteId: z.string().uuid().nullable().optional(),
});

export const updateCombatSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  sessionNoteId: z.string().uuid().nullable().optional(),
  mapNoteId: z.string().uuid().nullable().optional(),
});

export const combatantKindSchema = z.enum(['pc', 'npc', 'monster', 'other']);

export const addCombatantsSchema = z
  .object({
    noteId: z.string().uuid().optional(),
    name: z.string().trim().min(1).max(200).optional(),
    kind: combatantKindSchema.optional(),
    count: z.number().int().min(1).max(30).default(1),
    hpMax: z.number().int().min(0).max(99999).optional(),
    ac: z.number().int().min(0).max(99).optional(),
    initBonus: z.number().int().min(-20).max(50).optional(),
    initiative: z.number().int().min(-20).max(99).nullable().optional(),
    hidden: z.boolean().optional(),
  })
  .refine((v) => !!v.noteId || !!v.name, { message: 'Notiz oder Name angeben' });

export const updateCombatantSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  initiative: z.number().int().min(-20).max(99).nullable().optional(),
  initBonus: z.number().int().min(-20).max(50).optional(),
  hpCurrent: z.number().int().min(0).max(99999).nullable().optional(),
  hpMax: z.number().int().min(0).max(99999).nullable().optional(),
  tempHp: z.number().int().min(0).max(99999).optional(),
  ac: z.number().int().min(0).max(99).nullable().optional(),
  conditions: z.array(conditionSchema).max(30).optional(),
  concentration: z.boolean().optional(),
  defeated: z.boolean().optional(),
  deathSaves: z.object({ success: z.number().int().min(0).max(3), failure: z.number().int().min(0).max(3) }).optional(),
  hidden: z.boolean().optional(),
  notes: z.string().max(2000).optional(),
  sortOrder: z.number().int().optional(),
  /** Initiative für alle gleichen Gegner der Gruppe übernehmen. */
  applyToGroup: z.boolean().optional(),
});

export const hpChangeSchema = z.object({
  kind: z.enum(['damage', 'heal', 'temp', 'set']),
  amount: z.number().int().min(0).max(99999),
});

export const turnSchema = z.object({ direction: z.union([z.literal(1), z.literal(-1)]).default(1) });

export const endCombatSchema = z.object({ writeToSession: z.boolean().default(true) });

export interface CombatantDTO {
  id: string;
  name: string;
  kind: 'pc' | 'npc' | 'monster' | 'other';
  noteId: string | null;
  noteTitle: string | null;
  initiative: number | null;
  initBonus: number;
  sortOrder: number;
  hpCurrent: number | null;
  hpMax: number | null;
  tempHp: number;
  ac: number | null;
  conditions: { key: string; rounds: number | null; note?: string }[];
  concentration: boolean;
  defeated: boolean;
  deathSaves: { success: number; failure: number };
  hidden: boolean;
  groupId: string | null;
  notes: string;
}

export interface CombatDTO {
  id: string;
  campaignId: string;
  name: string;
  status: 'prep' | 'active' | 'ended';
  round: number;
  activeCombatantId: string | null;
  sessionNoteId: string | null;
  /** Kampfkarte (Notiz vom Typ `karte`). */
  mapNoteId: string | null;
  log: { at: string; round: number; text: string }[];
  /** In Initiative-Reihenfolge. */
  combatants: CombatantDTO[];
  createdAt: string;
  updatedAt: string;
}

export interface CombatListEntry {
  id: string;
  name: string;
  status: 'prep' | 'active' | 'ended';
  round: number;
  combatantCount: number;
  updatedAt: string;
}

/** Antwort auf Schaden/Heilung und Zugwechsel: Kampf + Hinweise (z. B. Konzentration). */
export interface CombatActionResult {
  combat: CombatDTO;
  notices: string[];
  concentrationDc?: number | null;
}

// ---------- Notizen mit Eigenschaften (Kanban, Gruppe, Dashboard) ----------

export interface NoteWithProps extends NoteIndexEntry {
  frontmatter: Record<string, unknown>;
}

export const patchFrontmatterSchema = z.object({
  set: z.record(z.string().min(1).max(100), z.unknown()).default({}),
  unset: z.array(z.string().min(1).max(100)).max(50).default([]),
});

export const appendLogSchema = z.object({
  text: z.string().trim().min(1).max(2000),
  /** Ortszeit des DMs (HH:MM), der Server läuft meist in UTC. */
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
});
